import type { UsersListPagination } from "./usersListTypes";

/**
 * ASSUMPTION (response shape of `GET api/v1/users` was not provided):
 * the body is assumed to look like
 *   { users: [...], pagination: { page, page_size, total, total_pages } }
 * (modelled on `GET api/v1/tenants`). To stay safe the mapper also tolerates:
 *  - items under `users` | `items` | `data` (array) | `results`, or a bare array body;
 *  - an optional `{ success, data }` envelope where `data` is the object or the array;
 *  - pagination under `pagination` | `meta`, or as top-level fields, in snake_case
 *    or camelCase (page, page_size/pageSize/limit/per_page, total/count/total_count/
 *    totalCount, total_pages/totalPages).
 * Rows may be snake_case or camelCase; row mapping is done by each feature's
 * `mapTenantUserTo*` adapter function.
 * If the real contract differs, this file is the only place to adjust.
 */

const ITEM_KEYS = ["users", "items", "data", "results"] as const;
const PAGINATION_KEYS = ["pagination", "meta"] as const;

export function asRecord(v: unknown): Record<string, unknown> | null {
  return v !== null && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

function findItems(container: Record<string, unknown>): unknown[] | null {
  for (const key of ITEM_KEYS) {
    const value = container[key];
    if (Array.isArray(value)) return value;
  }
  return null;
}

export function unwrapUsersListBody(body: unknown): {
  items: unknown[];
  pagination: unknown;
} {
  if (Array.isArray(body)) return { items: body, pagination: undefined };
  const root = asRecord(body);
  if (!root) return { items: [], pagination: undefined };

  const data = asRecord(root.data);
  // Search the root first, then the `{ success, data: {...} }` envelope.
  for (const container of data ? [root, data] : [root]) {
    const items = findItems(container);
    if (!items) continue;
    // Prefer a nested pagination object (container first, then root); otherwise
    // fall back to top-level fields of the container.
    const nested = [container, root]
      .flatMap((c) => PAGINATION_KEYS.map((k) => asRecord(c[k])))
      .find((p) => p !== null);
    return { items, pagination: nested ?? container };
  }
  return { items: [], pagination: undefined };
}

function toNonNegativeNumber(v: unknown): number | null {
  if (typeof v === "string" && v.trim() === "") return null;
  if (typeof v !== "number" && typeof v !== "string") return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function firstNumber(
  source: Record<string, unknown>,
  ...keys: string[]
): number | null {
  for (const key of keys) {
    const n = toNonNegativeNumber(source[key]);
    if (n !== null) return n;
  }
  return null;
}

export function mapUsersPagination(
  raw: unknown,
  fallback: { page: number; pageSize: number; rowCount: number },
): UsersListPagination {
  const p = asRecord(raw) ?? {};
  const page = firstNumber(p, "page") || fallback.page;
  const pageSize =
    firstNumber(p, "page_size", "pageSize", "limit", "per_page") ||
    fallback.pageSize;
  const total =
    firstNumber(p, "total", "count", "total_count", "totalCount") ??
    fallback.rowCount;
  const totalPages =
    firstNumber(p, "total_pages", "totalPages") ??
    Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  return { page, pageSize, total, totalPages };
}
